import type { EditorReadyFn, EditorReadyOptions, EditorSDK } from '@wix/platform-editor-sdk';

import { createBIService } from '../../../utils/bi';
import { interactionEnded, interactionFailed, interactionStarted, log, toMonitored } from '../../../utils/monitoring';
import { withTimeoutMonitor } from '../../../utils/promise-timeout';
import * as applicationState from '../../applicationState';
import { APP_TOKEN } from '../../constants';
import {
  maybeFixBrokenMenuItems,
  maybeFixLoginBarResponsiveLayout,
  maybeSetManagingAppDefIdForMAPages,
  removeBrokenInstallation,
  verifyMyAccountPage,
} from '../../data-fixers';
import { maybeFixBrokenMenusData } from '../../data-fixers/fixBrokenMenus';
import { startSequentialPromises, stopSequentialPromises } from '../../enforceSequentiality';
import * as membersLogic from '../../membersLogic';
import { registerAlwaysAvailableApps } from '../../services/integration';
import * as pagesService from '../../services/pages';
import * as componentsWrapper from '../../wrappers/components';

const INSTALLATION_TIMEOUT_AMOUNT = 15000;

async function maybeInstallMembersArea(editorSDK: EditorSDK, options: EditorReadyOptions) {
  if (!(await membersLogic.shouldInstall(editorSDK, options.firstInstall))) {
    return;
  }

  try {
    const monitoredPromise = () => toMonitored('install', () => membersLogic.install(editorSDK, options));
    await withTimeoutMonitor('installation', INSTALLATION_TIMEOUT_AMOUNT, monitoredPromise);
  } catch (e) {
    log('Removing initial installation as it failed', { extra: { error: (e as Error).toString() } });
    await removeBrokenInstallation(editorSDK, true, true);
    throw e;
  }
}

async function maybeSetSignUpPrivacyNoteType(editorSDK: EditorSDK, { firstInstall }: EditorReadyOptions) {
  try {
    if (firstInstall) {
      editorSDK.siteMembers.setPrivacyNoteType(APP_TOKEN, 'NOTE');
    }
  } catch {
    log('Failed to set privacyNoteType');
  }
}

export const editorReady: EditorReadyFn = async (editorSDK, _appToken, options) => {
  const biService = await createBIService({ editorSDK, options });

  try {
    interactionStarted('editorReady');

    const editorReadyTransaction = async () => {
      await maybeSetManagingAppDefIdForMAPages({ editorSDK, options });

      await pagesService.setStateForPages(editorSDK);

      // MA-84 investigation, making sure My Account page is always there as it has to be
      await verifyMyAccountPage(options, editorSDK);

      // Install MA and delete it if anything goes wrong
      await maybeInstallMembersArea(editorSDK, options);

      // Try to fix broken menus data
      await maybeFixBrokenMenusData(editorSDK);

      // Try to solve some issues like duplicated menu items and etc, where MA is corrupted but doesn't have to be deleted
      await maybeFixBrokenMenuItems(editorSDK);

      // Try to fix login bar responsive layout on editorX
      await maybeFixLoginBarResponsiveLayout(editorSDK);

      // Set privacyNoteType on first install
      await maybeSetSignUpPrivacyNoteType(editorSDK, options);

      const successfullyInstalled = await applicationState.isApplicationReady(editorSDK);

      if (!successfullyInstalled) {
        stopSequentialPromises('Members Area was not installed.');
      }

      await registerAlwaysAvailableApps(editorSDK);
      await componentsWrapper.registerToComponentAddedToStageEvent(editorSDK);
      startSequentialPromises();
    };

    await editorReadyTransaction();

    interactionEnded('editorReady');
  } catch (e) {
    const errorMessage = typeof e === 'string' ? e : (e as Error).message;
    biService.logInstallationFailure(errorMessage);
    interactionFailed('editorReady', e as Error);
    console.error('Members Area installation failed');
    console.error(e);
    stopSequentialPromises(e);
    throw e;
  }
};
